import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { YearList } from "./components/YearList";
import { MonthsList } from "./components/MonthList";
import { TimePicker } from "./components/TimePicker";
import { motion } from "framer-motion";
import { useDatePicker } from "./hooks/useDatePicker";
import { createPortal } from "react-dom";
import { useMemo, useRef, useState, useLayoutEffect } from "react";
import { appAreaWidth } from "@/lib/layout/appArea";
import {
  extractWallParts,
  todayWallParts,
  wallPartsToIso,
} from "@/lib/utils/datetime-tz";

type DatePickerProps = {
  closeDatePicker: () => void;
  border?: string;
  position: { top: number; left: number; showAbove?: boolean };
  time: boolean;
  defaultHour?: number;
  initialDate?: Date;
  onSelectDate: (date: Date) => void;
  /**
   * Optional inclusive day bounds. Out-of-range days render disabled and
   * navigation arrows clamp to months that hold at least one selectable day.
   * Compared in the picker's `timeZone` wall frame. `undefined` = no bound, so
   * existing callers are unaffected.
   */
  minDate?: Date;
  maxDate?: Date;
  /**
   * IANA timezone the picker should treat as the wall-clock frame. When set,
   * the calendar grid, TimePicker inputs, and emitted Date all reflect this
   * tz — the picker's internal `currentDate` is a "synthetic" Date whose UTC
   * fields hold the wall parts in `timeZone`. Defaults to the browser tz.
   */
  timeZone?: string;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
};

const BROWSER_TZ = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Build a synthetic Date whose UTC fields are the wall-clock parts in `tz`.
 * The picker subtree (TimePicker, MonthList, YearList) reads/writes those UTC
 * fields directly, so it sees "wall time" without needing tz-aware accessors.
 * The synthetic Date is converted to a real UTC ISO via `wallPartsToIso`
 * before bubbling up to `onSelectDate`.
 */
function buildInitialSynthetic(
  initialDate: Date | undefined,
  defaultHour: number | undefined,
  tz: string,
): Date {
  if (initialDate) {
    const parts = extractWallParts(initialDate.toISOString(), tz);
    return new Date(
      Date.UTC(parts.year, parts.month, parts.day, parts.hour, parts.minute),
    );
  }
  const today = todayWallParts(tz);
  return new Date(
    Date.UTC(
      today.year,
      today.month,
      today.day,
      defaultHour ?? today.hour,
      defaultHour !== undefined ? 0 : today.minute,
    ),
  );
}

export const DatePicker: React.FC<DatePickerProps> = ({
  closeDatePicker,
  border = "border outline-tertiary",
  position,
  time,
  defaultHour,
  initialDate,
  onSelectDate,
  minDate,
  maxDate,
  timeZone,
}) => {
  const tz = timeZone ?? BROWSER_TZ();

  // Inclusive day bounds as comparable keys (year*10000 + month0*100 + day) in
  // the picker's wall frame. null = unbounded on that side.
  const { minKey, maxKey } = useMemo(() => {
    const toKey = (d: Date | undefined): number | null => {
      if (!d) return null;
      const p = extractWallParts(d.toISOString(), tz);
      return p.year * 10000 + p.month * 100 + p.day;
    };
    return { minKey: toKey(minDate), maxKey: toKey(maxDate) };
  }, [minDate, maxDate, tz]);

  const dayKey = (year: number, month: number, day: number) =>
    year * 10000 + month * 100 + day;
  const isOutOfRange = (year: number, month: number, day: number) => {
    const k = dayKey(year, month, day);
    return (minKey !== null && k < minKey) || (maxKey !== null && k > maxKey);
  };
  const synthInitial = useMemo(
    () => buildInitialSynthetic(initialDate, defaultHour, tz),
    // intentional: only seed on mount; tz changes are handled by callers
    // remounting the picker (open→close→open) or by re-passing initialDate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Wrap the consumer's onSelectDate so we convert the synthetic wall-Date
  // back into a real UTC instant before bubbling.
  const emitSelectDate = (synth: Date) => {
    const iso = wallPartsToIso(
      {
        year: synth.getUTCFullYear(),
        month: synth.getUTCMonth(),
        day: synth.getUTCDate(),
        hour: synth.getUTCHours(),
        minute: synth.getUTCMinutes(),
      },
      tz,
    );
    onSelectDate(new Date(iso));
  };
  const {
    currentDate,
    months,
    dayNames,
    calendarDays,
    handlePrevMonth,
    handleNextMonth,
    setCurrentDate,
    handleSelectDay,
    // Date+time fields defer the commit to the TimePicker's save button, so a
    // day click doesn't apply-and-close before the time is set.
  } = useDatePicker(synthInitial, emitSelectDate, closeDatePicker, !time);

  const pickerRef = useRef<HTMLDivElement>(null);
  const portalRootRef = useRef<HTMLDivElement>(null);

  // Shield outside popovers/sheets from closing when the user clicks inside the
  // portaled calendar. Stops mousedown/pointerdown/touchstart from bubbling
  // past document.body once they have reached the React-delegated listener,
  // so document-level outside-click handlers never see them.
  useLayoutEffect(() => {
    const stopIfInPortal = (e: Event) => {
      const root = portalRootRef.current;
      if (root && e.target instanceof Node && root.contains(e.target)) {
        e.stopPropagation();
      }
    };
    const body = document.body;
    body.addEventListener("mousedown", stopIfInPortal);
    body.addEventListener("pointerdown", stopIfInPortal);
    body.addEventListener("touchstart", stopIfInPortal, { passive: true });
    return () => {
      body.removeEventListener("mousedown", stopIfInPortal);
      body.removeEventListener("pointerdown", stopIfInPortal);
      body.removeEventListener("touchstart", stopIfInPortal);
    };
  }, []);

  const [adjustedLeft, setAdjustedLeft] = useState(position.left);
  const [adjustedTop, setAdjustedTop] = useState<number | undefined>(
    position.showAbove ? undefined : position.top + 8,
  );
  const [adjustedBottom, setAdjustedBottom] = useState<number | undefined>(
    position.showAbove ? position.top + 8 : undefined,
  );

  const [headerEditing, setHeaderEditing] = useState(false);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [draftMonth, setDraftMonth] = useState(currentDate.getUTCMonth());
  const [draftYear, setDraftYear] = useState(String(currentDate.getUTCFullYear()));

  const openHeaderEdit = () => {
    setDraftMonth(currentDate.getUTCMonth());
    setDraftYear(String(currentDate.getUTCFullYear()));
    setHeaderEditing(true);
  };

  const closeHeaderEdit = () => {
    setMonthPickerOpen(false);
    setHeaderEditing(false);
  };

  const applyHeaderEdit = () => {
    const yearNum = parseInt(draftYear, 10);
    if (!Number.isFinite(yearNum) || yearNum < 1 || yearNum > 9999) {
      closeHeaderEdit();
      return;
    }
    setCurrentDate(
      new Date(
        Date.UTC(
          yearNum,
          draftMonth,
          1,
          currentDate.getUTCHours(),
          currentDate.getUTCMinutes(),
        ),
      ),
    );
    closeHeaderEdit();
  };

  const stepYear = (delta: number) => {
    const current = parseInt(draftYear, 10);
    const base = Number.isFinite(current) ? current : currentDate.getUTCFullYear();
    const next = Math.min(9999, Math.max(1, base + delta));
    setDraftYear(String(next));
  };

  // Disable a month-arrow when the adjacent month holds no selectable day.
  const prevMonthLast = new Date(
    Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 0),
  );
  const nextMonthFirst = new Date(
    Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, 1),
  );
  const prevDisabled =
    minKey !== null &&
    dayKey(
      prevMonthLast.getUTCFullYear(),
      prevMonthLast.getUTCMonth(),
      prevMonthLast.getUTCDate(),
    ) < minKey;
  const nextDisabled =
    maxKey !== null &&
    dayKey(
      nextMonthFirst.getUTCFullYear(),
      nextMonthFirst.getUTCMonth(),
      nextMonthFirst.getUTCDate(),
    ) > maxKey;

  useLayoutEffect(() => {
    if (pickerRef.current) {
      const pickerWidth = pickerRef.current.offsetWidth;
      const pickerHeight = pickerRef.current.offsetHeight;
      // Beside the docked Velo rail, not the raw viewport — the picker is
      // `fixed`, so no ancestor padding can keep it out from under the rail.
      const viewportWidth = appAreaWidth();
      const viewportHeight = window.innerHeight;
      const margin = 8;

      // Clamp horizontal position — prefer right-aligning to trigger when it overflows
      let left = position.left;
      if (left + pickerWidth > viewportWidth - margin) {
        left = viewportWidth - pickerWidth - margin;
      }
      setAdjustedLeft(Math.max(margin, left));

      // Vertical positioning
      // Room above the trigger, in the frame `bottom` is consumed in. The
      // positioner flips to "above" on its own 240px dropdown rule; this
      // picker is 332–372px tall, so "above" from the hook is a HINT, not a
      // fit — a trigger in the middle of a short window was flipped above and
      // the calendar's header ran off the top of the screen.
      const roomAbove = position.showAbove
        ? viewportHeight - position.top - margin
        : Number.POSITIVE_INFINITY;
      if (position.showAbove && pickerHeight <= roomAbove) {
        // showAbove: position.top is distance from bottom of viewport to top of trigger
        const bottomValue = position.top + margin;
        setAdjustedBottom(bottomValue);
        setAdjustedTop(undefined);
      } else if (position.showAbove) {
        // Above was the better side and still too short: clamp the panel's
        // BOTTOM edge into the viewport, exactly as the "neither fits" branch
        // below does, so Apply stays reachable and the header stays visible.
        setAdjustedTop(Math.max(margin, viewportHeight - margin - pickerHeight));
        setAdjustedBottom(undefined);
      } else {
        // Show below: position.top is rect.bottom
        let topValue = position.top + margin;
        // If it would overflow the bottom, flip to show above
        if (topValue + pickerHeight > viewportHeight - margin) {
          // position.top is rect.bottom, so rect.top ≈ position.top - triggerHeight
          // Use bottom positioning: distance from bottom = viewportHeight - rect.bottom + triggerHeight + margin
          // Simpler: just make sure it fits
          const flippedTop = position.top - pickerHeight - margin;
          if (flippedTop > margin) {
            topValue = flippedTop;
          } else {
            // PB-N0-1 — neither below nor above fits: CLAMP into the viewport.
            //
            // This branch used to fall through and keep the overflowing
            // `topValue`, leaving the panel hanging off the bottom of the
            // screen. Since the date+time picker defers its commit to the
            // Apply button in its BOTTOM row, and the panel is
            // `position: fixed`, that made the only commit affordance both
            // invisible and unreachable — the page behind cannot be scrolled
            // to reveal a fixed element, and every click landed on <html>.
            // For a trigger sitting mid-viewport on a ~720px-tall window this
            // was the ordinary case, not an edge one.
            //
            // Clamping so the panel's BOTTOM edge stays inside the viewport
            // keeps Apply on screen. The panel already carries
            // `max-h-[85dvh] overflow-y-auto`, so on a viewport too short for
            // the picker at all, the content scrolls INTERNALLY and Apply is
            // still reachable — which the old code could not do either, since
            // it never bounded the top.
            topValue = Math.max(margin, viewportHeight - margin - pickerHeight);
          }
        }
        setAdjustedTop(topValue);
        setAdjustedBottom(undefined);
      }
    }
  }, [position.left, position.top, position.showAbove]);

  return createPortal(
    <div
      ref={portalRootRef}
      // The calendar owns Escape. It had NO Escape handler at all, which broke
      // both ways: inside a popup whose form listens on the React tree the key
      // closed the WHOLE editor (a body portal still bubbles through the React
      // tree), and inside a HubDialogShell popup it did nothing at all (the
      // shell's "target outside me" probe bailed out first). The marker tells
      // such a host to leave the key to us; see HubDialogShell.OVERLAY_OPEN.
      data-escape-owner="true"
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        closeDatePicker();
      }}
      className="fixed left-0 top-0 flex justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none items-center h-dvh w-screen z-9999 cursor-default"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="fixed top-0 left-0 w-screen h-screen cursor-default"
        onClick={(e) => {
          closeDatePicker();
          e.stopPropagation();
        }}
      ></div>
      <div
        ref={pickerRef}
        style={{
          ...(adjustedTop !== undefined
            ? { top: `${adjustedTop}px` }
            : { bottom: `${adjustedBottom}px` }),
          left: `${adjustedLeft}px`,
        }}
        className={`${border} shadow-lg fixed surface-primary rounded-[10px] py-4 z-9999 flex w-max ${
          time ? "h-93" : "h-83"
        } max-h-[85dvh] overflow-y-auto outline-quaternary`}
      >
        {/* Calendar View */}
        <div className="flex h-full flex-col pl-4 pr-2">
          {/* Header - Month and Year */}
          <div className="flex justify-between items-center mb-4">
            <motion.button
              onClick={handlePrevMonth}
              disabled={prevDisabled}
              className={`p-1 ${prevDisabled ? "opacity-30 cursor-not-allowed" : ""}`}
              whileHover={prevDisabled ? undefined : { scale: 1.2 }}
              whileTap={prevDisabled ? undefined : { scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <IconChevronLeft size={20} />
            </motion.button>

            {headerEditing ? (
              <div className="flex gap-1.5 items-center">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setMonthPickerOpen((o) => !o)}
                    className="flex items-center gap-1 surface-primary body-2-regular border outline-tertiary rounded-md px-2 py-1 hover:surface-secondary"
                  >
                    <span>{months[draftMonth]}</span>
                    <IconChevronDown size={14} className="font-tertiary" />
                  </button>
                  {monthPickerOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setMonthPickerOpen(false)}
                      />
                      <div className="absolute left-0 top-full mt-1 z-20 surface-primary shadow-lg rounded-lg border outline-quaternary p-1 max-h-60 overflow-y-auto custom-scrollbar">
                        {months.map((m, i) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => {
                              setDraftMonth(i);
                              setMonthPickerOpen(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded ${
                              i === draftMonth
                                ? "cta-primary font-light-primary body-2-medium"
                                : "body-2-regular hover:surface-secondary"
                            }`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    className="w-14 surface-primary body-2-regular text-center border outline-tertiary rounded-md px-2 py-1 focus:focus-primary focus:outline-hidden"
                    value={draftYear}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d+$/.test(v)) setDraftYear(v);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyHeaderEdit();
                      // Innermost overlay wins: Escape here closes only the
                      // month/year editor, not the calendar around it.
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        closeHeaderEdit();
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        stepYear(1);
                      }
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        stepYear(-1);
                      }
                    }}
                    autoFocus
                  />
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Increase year"
                      onClick={() => stepYear(1)}
                      className="p-0.5 rounded hover:surface-secondary font-tertiary"
                    >
                      <IconChevronUp size={12} />
                    </button>
                    <button
                      type="button"
                      aria-label="Decrease year"
                      onClick={() => stepYear(-1)}
                      className="p-0.5 rounded hover:surface-secondary font-tertiary"
                    >
                      <IconChevronDown size={12} />
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Confirm"
                  onClick={applyHeaderEdit}
                  className="p-1 rounded-md hover:surface-secondary font-tertiary"
                >
                  <IconCheck size={16} />
                </button>
                <button
                  type="button"
                  aria-label="Cancel"
                  onClick={closeHeaderEdit}
                  className="p-1 rounded-md hover:surface-secondary font-tertiary"
                >
                  <IconX size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={openHeaderEdit}
                className="flex gap-1 px-2 py-0.5 rounded hover:surface-secondary"
              >
                <span className="body-1-regular">{months[currentDate.getUTCMonth()]}</span>
                <span className="body-1-regular">{currentDate.getUTCFullYear()}</span>
              </button>
            )}

            <motion.button
              onClick={handleNextMonth}
              disabled={nextDisabled}
              className={`p-1 ${nextDisabled ? "opacity-30 cursor-not-allowed" : ""}`}
              whileHover={nextDisabled ? undefined : { scale: 1.2 }}
              whileTap={nextDisabled ? undefined : { scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <IconChevronRight size={20} />
            </motion.button>
          </div>

          {/* Day names */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {dayNames.map((day) => (
              <div
                key={day}
                className="w-8 h-8 flex items-center justify-center body-3-regular font-quaternary"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1 shrink-0">
            {calendarDays.map((day, index) => {
              // Today highlight uses today's wall-clock date in the picker's
              // tz, not UTC — otherwise the highlight drifts off-by-one for
              // users far from the date line.
              const todayParts = todayWallParts(tz);
              const isCurrentDay =
                day.day === todayParts.day &&
                day.month === todayParts.month &&
                day.year === todayParts.year;

              const isSelectedDay =
                day.day === currentDate.getUTCDate() &&
                day.month === currentDate.getUTCMonth() &&
                day.year === currentDate.getUTCFullYear();

              const disabled = isOutOfRange(day.year, day.month, day.day);

              return (
                <button
                  key={index}
                  disabled={disabled}
                  onClick={() =>
                    disabled
                      ? undefined
                      : handleSelectDay(day.day, day.month, day.year)
                  }
                  className={`w-8 h-8 flex items-center justify-center rounded-lg body-3-medium
                        ${
                          disabled
                            ? "font-quaternary opacity-40 cursor-not-allowed"
                            : isSelectedDay
                              ? "cta-primary font-light-primary"
                              : !day.isCurrentMonth
                                ? "font-tertiary hover:surface-secondary"
                                : `font-primary hover:surface-secondary ${
                                    isCurrentDay ? "border outline-blue" : ""
                                  }`
                        }
                      `}
                >
                  {day.day}
                </button>
              );
            })}
          </div>

          {/* Time picker */}
          {time && (
            <TimePicker
              onSelectDate={emitSelectDate}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
            />
          )}
        </div>

        <div className="hidden md:contents">
          <MonthsList
            months={months}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
          />
          <YearList currentDate={currentDate} setCurrentDate={setCurrentDate} />
        </div>
      </div>
    </div>,
    document.body,
  );
};
